import { useRouter } from "vue-router";
import { useClientContextStore } from "../../store/eplex/eplexClientContext";
import { usePersonalizationStore } from "../../store/eplex/eplexPersonalization";
import { getProfileSettings } from "../../api/profileApi";
import { buildSignalAddInfo } from "../helpers/signal/buildSignalAddInfo";
import { SERVICE_WORKER_URL } from "../constants/pwa";
import { SIGNAL_WIDGET_ROOT_ID } from "../constants/signalWidget";

export interface SignalWidgetMountOptions {
  /** Не показывать вводное окно виджета про уведомления — пока идёт онбординг кабинета. */
  suppressOnboardingPrompt?: boolean;
}

const SCRIPT_SELECTOR = "script[data-signal-widget]";

// Состояние общее на всё приложение: виджет один, кто бы его ни запросил —
// MainLayout после онбординга или «Настройте за меня» посреди него.
let scriptLoading: Promise<boolean> | null = null;
let mounting: Promise<boolean> | null = null;

/** Виджет смонтирован — SDK держит свой корневой узел в DOM (teardown его убирает). */
export function isSignalWidgetMounted(): boolean {
  return document.getElementById(SIGNAL_WIDGET_ROOT_ID) !== null;
}

function loadSignalWidgetScript(): Promise<boolean> {
  // После teardown тег скрипта удалён — прежнее обещание больше ничего не значит.
  if (!document.querySelector(SCRIPT_SELECTOR)) scriptLoading = null;
  if (scriptLoading) return scriptLoading;

  const widgetUrl = import.meta.env.VITE_SIGNAL_WIDGET_URL;
  if (!widgetUrl) return Promise.resolve(false);

  scriptLoading = new Promise<boolean>((resolve) => {
    const script = document.createElement("script");
    script.setAttribute("data-signal-widget", "");
    script.src = widgetUrl;
    script.onload = () => resolve(true);
    script.onerror = () => {
      // SDK уже в памяти с прошлой загрузки (teardown убирает только тег) —
      // пользуемся им, а не считаем виджет недоступным.
      if (window.SignalWidget?.mount) {
        resolve(true);
        return;
      }
      script.remove();
      scriptLoading = null;
      resolve(false);
    };
    document.head.appendChild(script);
  });
  return scriptLoading;
}

// Доп. информация о пользователе для Сигнала (телефон, почта и т.п.).
// Профиль опционален: при ошибке монтируем виджет без addInfo.
async function resolveSignalAddInfo(): Promise<Record<string, string> | undefined> {
  try {
    const profile = await getProfileSettings();
    const addInfo = buildSignalAddInfo(profile);
    return Object.keys(addInfo).length > 0 ? addInfo : undefined;
  } catch (error) {
    console.error("Signal widget: не удалось получить профиль для addInfo: ", error);
    return undefined;
  }
}

/**
 * Подключение и монтирование чат-виджета «Сигнал» по данным контекста клиента.
 *
 * Личность для чата берём из контекста клиента, а не из разбора токена: в токене
 * намеренно нет ничего изменяемого, а подпись считает бэкенд ЛК (CAB-641, CAB-673).
 */
export function useSignalWidget() {
  const router = useRouter();
  const clientContextStore = useClientContextStore();
  const personalizationStore = usePersonalizationStore();

  // Переходы по ссылкам Сигнала того же origin (кнопки уведомлений/новостей) —
  // внутренней SPA-навигацией, без открытия новой вкладки и перезагрузки страницы.
  function navigateFromSignal(url: string): void {
    try {
      const parsed = new URL(url, window.location.origin);
      if (parsed.origin === window.location.origin) {
        router.push(parsed.pathname + parsed.search + parsed.hash);
        return;
      }
      window.open(url, "_blank", "noopener");
    } catch (error) {
      console.error("Signal widget navigation failed: ", error);
    }
  }

  async function mountFromContext(options: SignalWidgetMountOptions): Promise<boolean> {
    const apiUrl = import.meta.env.VITE_SIGNAL_API_URL;
    const wsUrl = import.meta.env.VITE_SIGNAL_WS_URL;
    const apiKey = import.meta.env.VITE_SIGNAL_API_KEY;
    if (!apiUrl || !apiKey || !window.SignalWidget?.mount) return false;

    await clientContextStore.load();

    const signalHash = clientContextStore.signalHash;
    const profileId = clientContextStore.profileId;

    // Подписи нет — чат просто не показываем. Вытаскивать человека из кабинета
    // из-за недоступного чата нельзя: чат не условие работы.
    if (!signalHash || profileId === null) return false;

    window.SignalWidget.mount({
      apiUrl,
      wsUrl: wsUrl ?? "",
      apiKey,
      userId: String(profileId),
      userName: clientContextStore.profileName,
      userHash: signalHash,
      theme: personalizationStore.theme,
      addInfo: await resolveSignalAddInfo(),
      onNavigate: navigateFromSignal,
      serviceWorkerUrl: SERVICE_WORKER_URL,
      suppressOnboardingPrompt: options.suppressOnboardingPrompt,
    });
    return true;
  }

  /**
   * Гарантирует, что виджет подключён и смонтирован. Повторный вызов без
   * suppressOnboardingPrompt снимает подавление вводного окна — так виджет,
   * поднятый посреди онбординга, после его завершения ведёт себя как обычно.
   */
  async function ensureMounted(options: SignalWidgetMountOptions = {}): Promise<boolean> {
    if (isSignalWidgetMounted()) {
      if (!options.suppressOnboardingPrompt) window.SignalWidget?.setOnboardingPromptSuppressed?.(false);
      return true;
    }
    if (mounting) return mounting;

    mounting = (async () => {
      try {
        if (!(await loadSignalWidgetScript())) return false;
        return await mountFromContext(options);
      } catch (error) {
        console.error("Signal widget mount failed: ", error);
        return false;
      } finally {
        mounting = null;
      }
    })();
    return mounting;
  }

  return { ensureMounted };
}
